import { useScenarioActivation } from '../contexts/ScenarioActivationContext';
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  Typography,
  keyframes
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import {
  SmartToy as BotIcon,
  Memory as ThreadIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  ExitToApp as ExitIcon,
  ArrowUpward as PendingArrowUpIcon,
  ArrowDownward as PendingArrowDownIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import { useNotification } from '../contexts/NotificationContext';
import { usePermissions } from '../contexts/PermissionContext';
import type { Scenario } from '../types/attacks';
import { EndingScenarioOverlay } from './EndingScenarioOverlay';
import { SCENARIO_CHANGED_EVENT } from '../constants/scenarioEvents';

type AttackStatus = 'running' | 'paused' | 'stopped';

const getStatusFromCommand = (command: any): AttackStatus => {
  const c = String(command || '').toLowerCase();
  if (c === 'run') return 'running';
  if (c === 'pause') return 'paused';
  return 'stopped';
};

const getStatusColor = (theme: Theme, status: AttackStatus) => {
  switch (status) {
    case 'running':
      return '#00ff88';
    case 'paused':
      return '#ffc107';
    case 'stopped':
      return '#ff3366';
    default:
      return theme.palette.text.secondary;
  }
};

const pendingFloat = keyframes`
  0% {
    transform: translateY(2px);
  }
  50% {
    transform: translateY(-2px);
  }
  100% {
    transform: translateY(2px);
  }
`;

export const ActiveAttackBar: React.FC = () => {
  const activateScenario = useScenarioActivation();
  const { selectedTenant } = useTenant();
  const { showNotification } = useNotification();
  const { hasPermission, isAdmin } = usePermissions();
  const theme = useTheme();

  const canControlScenario = isAdmin || (selectedTenant?.tenant_id ? hasPermission('access_operations', 'tenant', selectedTenant.tenant_id) : false);

  const [scenario, setScenario] = React.useState<Scenario | null>(null);
  const scenarioRef = React.useRef<Scenario | null>(null);
  const [status, setStatus] = React.useState<AttackStatus>('stopped');
  const [threads, setThreads] = React.useState<number>(1);
  const [botRunning, setBotRunning] = React.useState<number>(0);
  const [botPending, setBotPending] = React.useState<number>(0);
  const [botShuttingDown, setBotShuttingDown] = React.useState<number>(0);
  const [streamConnected, setStreamConnected] = React.useState(false);
  const [endingScenario, setEndingScenario] = React.useState(false);
  const awaitingUserStartRef = React.useRef(false);
  const useSSE = process.env.REACT_APP_ENABLE_SSE !== 'false';

  const hiddenOnRoute = false;

  React.useEffect(() => {
    if (hiddenOnRoute || !useSSE || !selectedTenant?.tenant_id) {
      return;
    }

    const cleanup = api.subscribeBotStatus(
      (data) => {
        setStreamConnected(true);
        const running = data?.running || 0;
        const pending = data?.pending || 0;
        const shuttingDown = data?.shutting_down || 0;
        setBotRunning(running);
        setBotPending(pending);
        setBotShuttingDown(shuttingDown);
        if (awaitingUserStartRef.current && (running > 0 || pending > 0 || shuttingDown > 0)) {
          awaitingUserStartRef.current = false;
          setStatus('running');
        }
      },
      (error) => {
        console.error('Bot status stream error:', error);
        setStreamConnected(false);
      }
    );

    return () => {
      cleanup?.();
      setStreamConnected(false);
    };
  }, [hiddenOnRoute, selectedTenant?.tenant_id, useSSE]);

  React.useEffect(() => {
    scenarioRef.current = scenario;
  }, [scenario]);

  const refresh = React.useCallback(async () => {
    if (!selectedTenant?.tenant_id) {
      setScenario(null);
      return;
    }
    const data = await api.getCurrentScenario();
    const current = data?.scenario ?? null;
    setScenario(current);
    if (!current) {
      return;
    }

    // Use embedded s3_config from single current-scenario response
    const s3 = data?.s3_config;
    if (s3?.s3_config) {
      const cmd = s3.s3_config.full_config?.command;
      if (!awaitingUserStartRef.current) {
        setStatus(getStatusFromCommand(cmd));
      }
      const t = s3.s3_config.threads ?? s3.s3_config.concurrent_requests;
      if (typeof t === 'number' && t > 0) {
        setThreads(t);
      }
    }

    if (!streamConnected) {
      // Bot running count
      try {
        const botStatus = await api.getDetailedBotStatus();
        const running = botStatus?.total_running || 0;
        const pending = botStatus?.total_pending || 0;
        const shuttingDown = botStatus?.total_shutting_down || 0;
        setBotRunning(running);
        setBotPending(pending);
        setBotShuttingDown(shuttingDown);
        if (awaitingUserStartRef.current && (running > 0 || pending > 0 || shuttingDown > 0)) {
          awaitingUserStartRef.current = false;
          setStatus('running');
        }
      } catch {
        // ignore
      }
    }
  }, [selectedTenant?.tenant_id, streamConnected]);

  React.useEffect(() => {
    if (hiddenOnRoute) return;
    let cancelled = false;
    let timeoutId: number | null = null;

    const tick = async () => {
      await refresh();
      if (cancelled) return;

      // If no active scenario, poll much less frequently to avoid UI flicker on selection screen.
      const hasActiveScenario = Boolean(scenarioRef.current);
      const nextDelayMs = hasActiveScenario ? 5_000 : 30_000;
      timeoutId = window.setTimeout(tick, nextDelayMs);
    };

    tick();

    return () => {
      cancelled = true;
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [refresh, hiddenOnRoute]);

  React.useEffect(() => {
    const handleScenarioChanged = (event: Event) => {
      const { detail } = event as CustomEvent<Scenario | null>;
      if (detail === null) {
        awaitingUserStartRef.current = false;
        setScenario(null);
        setStatus('stopped');
        setThreads(1);
        setBotRunning(0);
        setBotPending(0);
        setBotShuttingDown(0);
      } else if (detail && typeof detail === 'object') {
        awaitingUserStartRef.current = true;
        setScenario(detail);
        setStatus('stopped');
        setBotRunning(0);
        setBotPending(0);
        setBotShuttingDown(0);
      }
      refresh();
    };

    window.addEventListener(SCENARIO_CHANGED_EVENT, handleScenarioChanged);
    return () => {
      window.removeEventListener(SCENARIO_CHANGED_EVENT, handleScenarioChanged);
    };
  }, [refresh]);

  const handleStartPause = async () => {
    if (!scenario) return;
    try {
      if (status === 'running') {
        awaitingUserStartRef.current = false;
        await api.pauseAttack();
        setStatus('paused');
        showNotification('Attack paused', 'success');
      } else {
        if (!await activateScenario(scenario.id, undefined, {
          onActivating: active => { awaitingUserStartRef.current = !active; },
        })) return;
        setStatus('running');
        showNotification('Scenario set (running)', 'success');
      }
    } catch {
      showNotification('Failed to update attack status', 'error');
    }
  };

  const handleEndScenario = async () => {
    setEndingScenario(true);
    try {
      showNotification('Ending scenario...', 'info');
      await api.endScenario();
      window.dispatchEvent(new CustomEvent(SCENARIO_CHANGED_EVENT, { detail: null }));
      await refresh();
      showNotification('Scenario ended', 'success');
    } catch {
      showNotification('Failed to end scenario', 'error');
    } finally {
      awaitingUserStartRef.current = false;
      setEndingScenario(false);
    }
  };

  if (hiddenOnRoute) return null;
  if (!scenario) return null;

  const statusColor = getStatusColor(theme, status);
  const pendingDirection: 'up' | 'down' = botShuttingDown > 0 ? 'down' : 'up';
  const pendingCount = pendingDirection === 'down' ? botShuttingDown : botPending;

  return (
    <>
      <EndingScenarioOverlay open={endingScenario} />
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 900,
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(10, 14, 23, 0.92)' : 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(18px)',
          border: '1px solid rgba(0, 240, 255, 0.12)',
          borderRadius: 2,
          mb: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.25,
            gap: 2,
          }}
        >
        {/* Left */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 260 }}>
          <Box
            component={Link}
            to="/operations"
            sx={{
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              maxWidth: 320,
            }}
          >
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '0.95rem',
                maxWidth: 320,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                transition: 'color 0.2s ease',
                '&:hover': {
                  color: '#00f0ff',
                },
              }}
              title={scenario.name}
            >
              {scenario.name}
            </Typography>
          </Box>
          <Chip
            label={status.toUpperCase()}
            size="small"
            sx={{
              backgroundColor: `${statusColor}20`,
              border: `1px solid ${statusColor}40`,
              color: statusColor,
              fontFamily: '"Space Mono", monospace',
              fontWeight: 700,
              letterSpacing: '0.06em',
            }}
          />
        </Box>

        {/* Center */}
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 1.5,
              background: 'rgba(0, 240, 255, 0.08)',
              border: '1px solid rgba(0, 240, 255, 0.22)',
            }}
          >
            <BotIcon sx={{ color: '#00f0ff', fontSize: '1.2rem' }} />
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '1.15rem',
                fontWeight: 800,
                color: '#00f0ff',
                minWidth: 64,
                textAlign: 'center',
              }}
            >
              {botRunning}
            </Typography>
            {pendingCount > 0 && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.4,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    animation: `${pendingFloat} 1.4s ease-in-out infinite`,
                    transformOrigin: 'center'
                  }}
                >
                  {pendingDirection === 'down' ? (
                    <PendingArrowDownIcon
                      sx={{
                        color: '#00f0ff',
                        fontSize: '0.85rem',
                        filter: 'drop-shadow(0 0 6px rgba(0, 240, 255, 0.35))'
                      }}
                    />
                  ) : (
                    <PendingArrowUpIcon
                      sx={{
                        color: '#00f0ff',
                        fontSize: '0.85rem',
                        filter: 'drop-shadow(0 0 6px rgba(0, 240, 255, 0.35))'
                      }}
                    />
                  )}
                </Box>
                <Typography
                  sx={{
                    fontFamily: '"Space Mono", monospace',
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                  }}
                >
                  {pendingDirection === 'down' ? '-' : '+'}
                  {pendingCount}
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 1.5,
              background: 'rgba(255, 0, 110, 0.08)',
              border: '1px solid rgba(255, 0, 110, 0.22)',
            }}
          >
            <ThreadIcon sx={{ color: '#ff006e', fontSize: '1.2rem' }} />
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '1.15rem',
                fontWeight: 800,
                color: '#ff006e',
                minWidth: 64,
                textAlign: 'center',
              }}
            >
              {threads}
            </Typography>
          </Box>
        </Box>

        {/* Right: Pause and End Scenario only for users with access_operations */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 290, justifyContent: 'flex-end' }}>
          {canControlScenario && (
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={status === 'running' ? <PauseIcon /> : <PlayIcon />}
                onClick={handleStartPause}
                sx={{ fontFamily: '"Space Mono", monospace', fontWeight: 700, minWidth: 120 }}
              >
                {status === 'running' ? 'Pause' : status === 'paused' ? 'Resume' : 'Set'}
              </Button>

              <Button
                variant="contained"
                size="small"
                color="error"
                startIcon={<ExitIcon />}
                onClick={handleEndScenario}
                sx={{ fontFamily: '"Space Mono", monospace', fontWeight: 700, minWidth: 140 }}
              >
                End Scenario
              </Button>
            </>
          )}
        </Box>
      </Box>

        <Divider sx={{ borderColor: 'rgba(0, 240, 255, 0.12)' }} />
      </Box>
    </>
  );
};


